<template>
  <!-- Skip to main content for keyboard users -->
  <a href="#reset-password-main" class="visually-hidden skip-link">Skip to main content</a>

  <div class="modal-overlay" @click.self="closeModal" role="dialog" aria-modal="true" aria-labelledby="reset-password-title">
    <div class="modal-content-box">
      <button class="close-button" @click="closeModal" aria-label="Close reset password modal">&times;</button>
      <h1 id="reset-password-title" class="title">Reset Password</h1>
      <p class="message">Enter your new password below</p>
      
      <main id="reset-password-main">
        <form @submit.prevent="resetPassword" class="flex flex-column" novalidate>
          <div class="field-group">
            <label for="email-input">
              <input
                id="email-input"
                class="input"
                type="email"
                name="email"
                v-model="email"
                placeholder=""
                required
                autocomplete="email"
                aria-describedby="email-help"
                readonly
              />
              <span>Email</span>
            </label>
            <div id="email-help" class="visually-hidden">Your email address for password reset</div>
          </div>

          <div class="field-group">
            <label for="new-password-input" class="password-field">
              <input
                id="new-password-input"
                :class="['input', { 'input-error': errors.newPassword }]"
                :type="showNewPassword ? 'text' : 'password'"
                name="newPassword"
                v-model="newPassword"
                @input="validateNewPassword"
                autocomplete="new-password"
                placeholder=""
                required
                :aria-invalid="errors.newPassword ? 'true' : 'false'"
                aria-describedby="new-password-help new-password-error"
              />
              <span>New Password</span>
              <button
                type="button"
                class="password-toggle"
                @click="showNewPassword = !showNewPassword"
                :aria-label="showNewPassword ? 'Hide new password' : 'Show new password'"
                aria-describedby="new-password-input"
              >
                <svg v-if="showNewPassword" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-eye-icon lucide-eye" aria-hidden="true"><path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"/><circle cx="12" cy="12" r="3"/></svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-eye-off-icon lucide-eye-off" aria-hidden="true"><path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49"/><path d="M14.084 14.158a3 3 0 0 1-4.242-4.242"/><path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143"/><path d="m2 2 20 20"/></svg>
              </button>
            </label>
            <div id="new-password-help" class="visually-hidden">Password must be at least 6 characters with special characters</div>
          </div>

          <div class="field-group">
            <label for="confirm-password-input" class="password-field">
              <input
                id="confirm-password-input"
                :class="['input', { 'input-error': errors.confirmNewPassword }]"
                :type="showConfirmNewPassword ? 'text' : 'password'"
                name="confirmNewPassword"
                v-model="confirmNewPassword"
                @input="validateConfirmNewPassword"
                autocomplete="new-password"
                placeholder=""
                required
                :aria-invalid="errors.confirmNewPassword ? 'true' : 'false'"
                aria-describedby="confirm-password-help confirm-password-error"
              />
              <span>Confirm New Password</span>
              <button
                type="button"
                class="password-toggle"
                @click="showConfirmNewPassword = !showConfirmNewPassword"
                :aria-label="showConfirmNewPassword ? 'Hide confirm password' : 'Show confirm password'"
                aria-describedby="confirm-password-input"
              >
                <svg v-if="showConfirmNewPassword" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-eye-icon lucide-eye" aria-hidden="true"><path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"/><circle cx="12" cy="12" r="3"/></svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-eye-off-icon lucide-eye-off" aria-hidden="true"><path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49"/><path d="M14.084 14.158a3 3 0 0 1-4.242-4.242"/><path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143"/><path d="m2 2 20 20"/></svg>
              </button>
            </label>
            <div id="confirm-password-help" class="visually-hidden">Re-enter your new password to confirm</div>
          </div>

          <div v-if="errors.newPassword" id="new-password-error" class="field-error" role="alert">{{ errors.newPassword }}</div>
          <div v-if="errors.confirmNewPassword" id="confirm-password-error" class="field-error" role="alert">{{ errors.confirmNewPassword }}</div>

          <button
            class="submit"
            type="submit"
            :disabled="!canSubmit"
            :class="{ 'submit-disabled': !canSubmit }"
            :aria-label="isSubmitting ? 'Resetting password, please wait' : 'Reset password and continue'"
          >
            <span class="btn-title">{{ isSubmitting ? 'Resetting...' : 'Reset Password' }}</span>
            <span class="icon-arrow" aria-hidden="true">
              <svg
                width="66px"
                height="43px"
                viewBox="0 0 66 43"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <g fill="none" fill-rule="evenodd" id="arrow">
                  <path
                    id="arrow-icon-one"
                    d="M40.1543933,3.89485454 L43.9763149,0.139296592 C44.1708311,-0.0518420739 44.4826329,-0.0518571125 44.6771675,0.139262789 L65.6916134,20.7848311 C66.0855801,21.1718824 66.0911863,21.8050225 65.704135,22.1989893 C65.7000188,22.2031791 65.6958657,22.2073326 65.6916762,22.2114492 L44.677098,42.8607841 C44.4825957,43.0519059 44.1708242,43.0519358 43.9762853,42.8608513 L40.1545186,39.1069479 C39.9575152,38.9134427 39.9546793,38.5968729 40.1481845,38.3998695 C40.1502893,38.3977268 40.1524132,38.395603 40.1545562,38.3934985 L56.9937789,21.8567812 C57.1908028,21.6632968 57.193672,21.3467273 57.0001876,21.1497035 C56.9980647,21.1475418 56.9959223,21.1453995 56.9937605,21.1432767 L40.1545208,4.60825197 C39.9574869,4.41477773 39.9546013,4.09820839 40.1480756,3.90117456 C40.1501626,3.89904911 40.1522686,3.89694235 40.1543933,3.89485454 Z"
                    fill="#FFFFFF"
                  />
                  <path
                    id="arrow-icon-two"
                    d="M20.1543933,3.89485454 L23.9763149,0.139296592 C24.1708311,-0.0518420739 24.4826329,-0.0518571125 24.6771675,0.139262789 L45.6916134,20.7848311 C46.0855801,21.1718824 46.0911863,21.8050225 45.704135,22.1989893 C45.7000188,22.2031791 45.6958657,22.2073326 45.6916762,22.2114492 L24.677098,42.8607841 C24.4825957,43.0519059 24.1708242,43.0519358 23.9762853,42.8608513 L20.1545186,39.1069479 C19.9575152,38.9134427 19.9546793,38.5968729 20.1481845,38.3998695 C20.1502893,38.3977268 20.1524132,38.395603 20.1545562,38.3934985 L36.9937789,21.8567812 C37.1908028,21.6632968 37.193672,21.3467273 37.0001876,21.1497035 C36.9980647,21.1475418 36.9959223,21.1453995 36.9937605,21.1432767 L20.1545208,4.60825197 C19.9574869,4.41477773 19.9546013,4.09820839 20.1480756,3.90117456 C20.1501626,3.89904911 20.1522686,3.89694235 20.1543933,3.89485454 Z"
                    fill="#FFFFFF"
                  />
                  <path
                    id="arrow-icon-three"
                    d="M0.154393339,3.89485454 L3.97631488,0.139296592 C4.17083111,-0.0518420739 4.48263286,-0.0518571125 4.67716753,0.139262789 L25.6916134,20.7848311 C26.0855801,21.1718824 26.0911863,21.8050225 25.704135,22.1989893 C25.7000188,22.2031791 25.6958657,22.2073326 25.6916762,22.2114492 L4.67709797,42.8607841 C4.48259567,43.0519059 4.17082418,43.0519358 3.97628526,42.8608513 L0.154518591,39.1069479 C-0.0424848215,38.9134427 -0.0453206733,38.5968729 0.148184538,38.3998695 C0.150289256,38.3977268 0.152413239,38.395603 0.154556228,38.3934985 L16.9937789,21.8567812 C17.1908028,21.6632968 17.193672,21.3467273 17.0001876,21.1497035 C16.9980647,21.1475418 16.9959223,21.1453995 16.9937605,21.1432767 L0.15452076,4.60825197 C-0.0425130651,4.41477773 -0.0453986756,4.09820839 0.148075568,3.90117456 C0.150162624,3.89904911 0.152268631,3.89694235 0.154393339,3.89485454 Z"
                    fill="#FFFFFF"
                  />
                </g>
              </svg>
            </span>
          </button>

          <div v-if="errorMessage" class="error-message" role="alert" aria-live="polite">{{ errorMessage }}</div>
          <div v-if="successMessage" class="success-message" role="status" aria-live="polite">{{ successMessage }}</div>

          <nav class="signin">
            Remember your password?
            <a href="#" @click.prevent="goToLogin" aria-label="Go back to login page">
              Login
            </a>
          </nav>
        </form>
      </main>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { auth } from "../firebase";
import { confirmPasswordReset } from "firebase/auth";
import { closeModal, resetPasswordParams } from '@/composables/useAuthModal';

export default {
  name: "ResetPasswordModal",
  setup() {
    
    const email = ref("");
    const newPassword = ref("");
    const confirmNewPassword = ref("");
    const errorMessage = ref("");
    const successMessage = ref("");
    const isSubmitting = ref(false);
    const showNewPassword = ref(false);
    const showConfirmNewPassword = ref(false);
    
    const errors = ref({
      newPassword: "",
      confirmNewPassword: "",
    });

    const oobCode = ref("");

    const canSubmit = computed(() => {
      return !isSubmitting.value && 
             newPassword.value !== '' && 
             confirmNewPassword.value !== '' &&
             !errors.value.newPassword &&
             !errors.value.confirmNewPassword &&
             oobCode.value !== '';
    });

    onMounted(() => {
      if (resetPasswordParams.value) {
        email.value = resetPasswordParams.value.email || "";
        oobCode.value = resetPasswordParams.value.oobCode || "";
      }
      
      if (!oobCode.value) {
        errorMessage.value = "Invalid reset link. Please request a new password reset.";
      }
    });

    const validateNewPassword = () => {
      if (newPassword.value.length < 6) {
        errors.value.newPassword = "Password must be at least 6 characters.";
      } else if (!/[!@#$%^&*]/.test(newPassword.value)) {
        errors.value.newPassword = "Password must include at least one special character.";
      } else {
        errors.value.newPassword = "";
      }
    };

    const validateConfirmNewPassword = () => {
      if (!confirmNewPassword.value) {
        errors.value.confirmNewPassword = "Please confirm your password.";
      } else if (confirmNewPassword.value !== newPassword.value) {
        errors.value.confirmNewPassword = "Passwords do not match.";
      } else {
        errors.value.confirmNewPassword = "";
      }
    };

    const resetPassword = async () => {
      validateNewPassword();
      validateConfirmNewPassword();

      if (errors.value.newPassword || errors.value.confirmNewPassword) {
        return;
      }

      if (!oobCode.value) {
        errorMessage.value = "Invalid reset link. Please request a new password reset.";
        return;
      }

      isSubmitting.value = true;
      errorMessage.value = "";
      successMessage.value = "";

      try {
        await confirmPasswordReset(auth, oobCode.value, newPassword.value);
        successMessage.value = "Password reset successfully! Redirecting to login...";
        
        setTimeout(() => {
          closeModal();
        }, 2000);
      } catch (error) {
        switch (error.code) {
          case "auth/invalid-action-code":
          case "auth/expired-action-code":
            errorMessage.value = "Invalid or expired reset link. Please request a new password reset.";
            break;
          case "auth/weak-password":
            errorMessage.value = "Password is too weak. Please choose a stronger password with at least 6 characters and special characters.";
            break;
          case "auth/network-request-failed":
            errorMessage.value = "Network error. Please check your connection and try again.";
            break;
          case "auth/too-many-requests":
            errorMessage.value = "Too many attempts. Please try again later.";
            break;
          default:
            errorMessage.value = "Failed to reset password. Please try again.";
        }
      } finally {
        isSubmitting.value = false;
      }
    };

    const goToLogin = () => {
      closeModal();
    };

    return {
      email,
      newPassword,
      confirmNewPassword,
      errorMessage,
      successMessage,
      isSubmitting,
      showNewPassword,
      showConfirmNewPassword,
      errors,
      canSubmit,
      resetPassword,
      goToLogin,
      closeModal,
      validateNewPassword,
      validateConfirmNewPassword,
    };
  },
};
</script>

<style scoped>
/* ===== ACCESSIBILITY STYLES ===== */
.visually-hidden {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

.skip-link {
  position: absolute;
  top: -40px;
  left: 6px;
  background: #6566be;
  color: white;
  padding: 8px;
  text-decoration: none;
  border-radius: 4px;
  z-index: 10000;
  font-weight: 600;
  transition: top 0.2s ease;
}

.skip-link:focus {
  top: 6px;
}

.field-group {
  margin-bottom: 1rem;
}

/* Enhanced focus indicators for keyboard users */
.close-button:focus:not(:focus-visible),
.submit:focus:not(:focus-visible),
.password-toggle:focus:not(:focus-visible),
.input:focus:not(:focus-visible),
.signin a:focus:not(:focus-visible) {
  outline: none;
}

.close-button:focus-visible,
.submit:focus-visible,
.password-toggle:focus-visible,
.signin a:focus-visible {
  outline: 2px solid #6566be;
  outline-offset: 2px;
}

.input:focus-visible {
  outline: 2px solid #6566be;
  outline-offset: 1px;
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .submit,
  .close-button,
  .password-toggle {
    border-width: 3px;
  }

  .input {
    border-width: 2px;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .submit,
  .close-button,
  .password-toggle,
  .input,
  .modal-overlay,
  .modal-content-box {
    transition: none;
    animation: none;
  }

  .skip-link {
    transition: none;
  }

  .submit:hover .icon-arrow,
  .submit:hover #arrow-icon-one,
  .submit:hover #arrow-icon-two,
  .submit:hover #arrow-icon-three {
    animation: none;
    transform: none;
  }
}

/* Touch target size for mobile */
@media (max-width: 768px) {
  .submit,
  .close-button,
  .password-toggle {
    min-height: 44px;
    min-width: 44px;
  }

  .password-toggle {
    padding: 12px;
  }

  .close-button {
    width: 44px;
    height: 44px;
  }
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: linear-gradient(90deg, rgba(248, 177, 155, 0.2) 0%, rgba(101, 103, 190, 0.2) 100%);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
  animation: fadeIn 0.3s ease;
}

.close-button {
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  font-size: 24px;
  color: var(--tColor);
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;
  opacity: 0.7;
  z-index: 10;
}

.close-button:hover {
  background: rgba(101, 103, 190, 0.1);
  opacity: 1;
  transform: scale(1.1);
}

.modal-content-box {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 380px;
  width: 100%;
  padding: 28px;
  border-radius: 20px;
  position: relative;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%);
  color: var(--tColor);
  box-shadow: 
    0 20px 40px rgba(101, 103, 190, 0.15),
    0 10px 20px rgba(248, 177, 155, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(180, 142, 171, 0.2);
  animation: slideUp 0.4s ease;
}

.title {
  font-size: 24px;
  font-weight: 700;
  text-align: center;
  background: linear-gradient(135deg, var(--sColor) 0%, var(--bColor) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
  margin-bottom: 4px;
  letter-spacing: 0.02em;
}

.message {
  font-size: 13px;
  color: var(--tColor);
  text-align: center;
  line-height: 1.4;
  margin-bottom: 8px;
  opacity: 0.8;
}



.flex {
  display: flex;
  width: 100%;
  gap: 6px;
}

.flex-column {
  flex-direction: column;
}

.form label {
  position: relative;
}

.input {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.6) 100%);
  border: 1.5px solid rgba(209, 210, 230, 0.5);
  border-radius: 10px;
  padding: 11px 14px;
  width: 100%;
  font-size: 14px;
  font-family: var(--font-primary);
  font-weight: 500;
  color: var(--tColor);
  outline: 0;
  transition: all 0.3s ease;
}

.form label .input + span {
  color: var(--tColor);
  position: absolute;
  left: 10px;
  top: 0px;
  font-size: 0.85em;
  cursor: text;
  transition: 0.3s ease;
}

.form label .input:placeholder-shown + span {
  top: 11px;
  font-size: 0.85em;
}

.form label .input:focus + span,
.form label .input:valid + span {
  background: linear-gradient(135deg, var(--sColor) 0%, var(--aColor) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
  top: -8px;
  font-size: 0.75em;
  font-weight: 600;
  padding: 0 4px;
  background-color: white;
}

.input:focus {
  outline: none;
  border: 1.5px solid transparent;
  background: linear-gradient(white, white) padding-box,
              linear-gradient(135deg, var(--sColor) 0%, var(--aColor) 100%) border-box;
  box-shadow: 
    0 0 0 4px rgba(101, 103, 190, 0.1),
    0 4px 12px rgba(101, 103, 190, 0.15);
  transform: translateY(-1px);
}

.input-error {
  border: 1px solid #e74c3c !important;
}

.input-error + span {
  color: #e74c3c !important;
}

.password-field {
  position: relative;
}

.password-toggle {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--tColor);
  cursor: pointer;
  padding: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.password-toggle:hover {
  color: var(--sColor);
}

.password-toggle svg {
  width: 20px;
  height: 20px;
}

.submit {
  display: flex;
  align-items: center;
  padding: 11px 20px;
  text-decoration: none;
  font-size: 14px;
  color: #ffffff;
  background: linear-gradient(135deg, var(--sColor) 0%, var(--bColor) 100%);
  transition: all 0.3s ease;
  border-radius: 10px;
  box-shadow: 0 0 0.2em 0 var(--sColor);
  cursor: pointer;
  border: none;
  outline: none;
  margin-top: 6px;
  font-family: var(--font-primary);
  font-weight: 600;
}

.submit:hover:not(:disabled) {
  transform: translateY(1px);
  box-shadow: 0 0 0.4em 0 var(--sColor);
}

.submit .icon-arrow {
  transition: all 0.3s ease;
  margin-right: 0;
  transform: scale(0.3);
  margin-left: 8px;
  position: relative;
  top: 6%;
}

.submit:hover .icon-arrow {
  margin-right: 8px;
  transform: scale(0.35);
}

.submit #arrow-icon-one {
  transition: 0.4s;
  transform: translateX(-60%);
}

.submit #arrow-icon-two {
  transition: 0.5s;
  transform: translateX(-30%);
}

.submit:hover #arrow-icon-three {
  animation: color_anim 1s infinite 0.2s;
}

.submit:hover #arrow-icon-one {
  transform: translateX(0%);
  animation: color_anim 1s infinite 0.6s;
}

.submit:hover #arrow-icon-two {
  transform: translateX(0%);
  animation: color_anim 1s infinite 0.4s;
}

@keyframes color_anim {
  0% {
    fill: white;
  }
  50% {
    fill: var(--sColor);
  }
  100% {
    fill: white;
  }
}

.submit:disabled,
.submit-disabled {
  background-color: rgba(150, 150, 150, 0.3) !important;
  color: rgba(255, 255, 255, 0.5) !important;
  cursor: not-allowed !important;
  transform: none !important;
  box-shadow: none !important;
}

.field-error {
  color: #e74c3c;
  font-size: 11px;
  margin-top: -4px;
  margin-bottom: 4px;
}

.error-message {
  color: #e74c3c;
  font-size: 12px;
  text-align: center;
  margin-top: 6px;
}

.success-message {
  color: var(--sColor);
  font-size: 12px;
  text-align: center;
  margin-top: 6px;
}

.signin {
  font-size: 13px;
  color: var(--tColor);
  text-align: center;
  margin-top: 8px;
}

.signin a {
  background: linear-gradient(135deg, var(--aColor) 0%, var(--sColor) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
  text-decoration: none;
}

.signin a:hover {
  background: linear-gradient(135deg, var(--sColor) 0%, var(--aColor) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
  text-decoration: underline;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

</style>